import { showNotification } from "@mantine/notifications"
import { useMutation, useQuery } from "@tanstack/react-query"

import {
  LabelsExplorerResponses,
  AxesFeatureLabelsMetadata,
} from "@costory/types/endpoints/labelsExplorer"

import { apiClient } from "@costory/front/lib/apiClient"
import { queryClient } from "@costory/front/lib/queryClient"

export const useLabelsExplorerMutation = () => {
  return useMutation({
    mutationFn: async (data: LabelsExplorerResponses.LabelsExplorer) => {
      const response = await apiClient.post(`/labels-explorer/${data.id}`, data)
      return response.data
    },
    onSuccess: async ({ key }) => {
      await queryClient.invalidateQueries({
        queryKey: ["labels-explorer-stats"],
      })
      await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
      showNotification({
        color: "green",
        title: "Label updated!",
        message: `Label ${key} updated successfully!`,
      })
    },
    onError: (error: { error: string }) => {
      showNotification({ color: "red", title: "Error", message: error.error })
    },
  })
}

export const useBulkLabelProcessingMutation = () => {
  return useMutation({
    mutationFn: async (data: LabelsExplorerResponses.BulkLabelProcessing) => {
      const response =
        await apiClient.post<LabelsExplorerResponses.BulkLabelProcessingResponse>(
          "/labels-explorer/bulk-process",
          data,
        )
      return response.data
    },
    onSuccess: async (data) => {
      if (data.success) {
        await queryClient.invalidateQueries({
          queryKey: ["labels-explorer-stats"],
        })
        await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
        showNotification({
          color: "green",
          title: "Labels processed!",
          message: data.message,
        })
      } else {
        showNotification({
          color: "yellow",
          title: "Labels not processed",
          message: data.message,
        })
      }
    },
    onError: (error: { error: string }) => {
      showNotification({ color: "red", title: "Error", message: error.error })
    },
  })
}

export const useLabelsExplorerStatsQuery = () => {
  return useQuery({
    queryKey: ["labels-explorer-stats"],
    queryFn: async () => {
      const response = await apiClient.get<
        LabelsExplorerResponses.LabelsStatsFullBilling[]
      >("/labels-explorer/stats")
      return response.data
    },
  })
}

export const useAxesFeatureLabelsMutation = () => {
  return useMutation({
    mutationFn: async (data: {
      metadata: AxesFeatureLabelsMetadata
      onSuccess?: (metadata: AxesFeatureLabelsMetadata) => void
    }) => {
      const response = await apiClient.post(
        "/labels-explorer/axesFeatureLabels",
        { metadata: data.metadata },
      )
      return {
        data: response.data,
        onSuccess: data.onSuccess,
        metadata: data.metadata,
      }
    },
    onSuccess: async ({ onSuccess, metadata }) => {
      await queryClient.invalidateQueries({
        queryKey: ["axes-feature-labels"],
      })
      await queryClient.invalidateQueries({ queryKey: ["onboardingStatus"] })
      // Call the success callback to update local state
      onSuccess?.(metadata)
      showNotification({
        color: "green",
        title: "Transformations saved!",
        message: "Label transformations saved successfully!",
      })
    },
    onError: (error: { error: string }) => {
      showNotification({
        color: "red",
        title: "Error",
        message: error.error || "Something went wrong",
      })
    },
  })
}

export const useAxesFeatureLabelsQuery = () => {
  return useQuery({
    queryKey: ["axes-feature-labels"],
    queryFn: async () => {
      const response = await apiClient.get<{
        transformations: AxesFeatureLabelsMetadata
      }>("/labels-explorer/axesFeatureLabels")
      return response.data
    },
  })
}
